import React, { useEffect, useState } from "react";
import moment from 'moment'
import {
  // ExpandCircleDown as ExpandCircleDownIcon, 
  // ExpandCircleDown as ExpandCircleUpIcon, 
  AddCircleOutline as AddIcon,
  // DeleteOutline as DeleteIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import { ReactComponent as ExpandDownIcon } from '../icons/ExpandDown.svg';
import { ReactComponent as ExpandUpIcon } from '../icons/ExpandUp.svg';
import { ReactComponent as DeleteIcon } from '../icons/DeleteIcon.svg';

import LoadingScreen from "../pages/shared/LoadingScreen"
import { useKitchenHook } from "../hooks/resourcehooks";
import { ResourceOptionType, selectedDaysType, WeekdayOptionFields, DayScheduleType } from "../data/types";
import {  getPeriodStyles, isSameOrLess, roundToNearest30Minutes, getFullDayName } from "../utils/helpers";




type OperatingHoursListProps = {
resourceOptions: ResourceOptionType;
setResourceOptions: React.Dispatch<React.SetStateAction<ResourceOptionType>>
selectedDays: selectedDaysType
setSelectedDays: React.Dispatch<React.SetStateAction<selectedDaysType>>
};


const OperatingHoursList: React.FC<OperatingHoursListProps> = ({ resourceOptions: currentResourceOptions, setResourceOptions: setCurrentResourceOptions, selectedDays, setSelectedDays }) => {
  const daysOfWeek: Set<WeekdayOptionFields> = new Set([ "sun", "mon","tue",  "wed","thu",  "fri", "sat"] as WeekdayOptionFields[])
  // const [currentResourceOptions, setCurrentResourceOptions] = useState<ResourceOptionType>({} as ResourceOptionType);
  // const setCurrentResourceOptions = (options: ResourceOptionType) => {
  //   setResourceOptions(options)
  // }
  // console.log("selectedDays", selectedDays)

  const handleSelectDay = (dayName: WeekdayOptionFields, value: boolean) => {
    setSelectedDays(prevDays => {
        return {
            ...prevDays,
            [dayName]: value
        }
    })
    // Initialize default block when opening a closed day
    setCurrentResourceOptions(prev => {
      const safePrev: any = prev ?? {};
      const prevValue: any = safePrev[dayName];
      if (value) {
        if (Array.isArray(prevValue) && prevValue.length > 0){
          return prev as any
        }
        // If legacy single object exists, migrate it into an array; otherwise set default
        const looksLikeBlock = prevValue && typeof prevValue === 'object' && 'open_time' in prevValue && 'close_time' in prevValue;
        const defaultBlock: DayScheduleType = {
          open_time: '08:00 AM',
          close_time: '08:00 PM',
          period_type: 'STANDARD'
        }
        return {
          ...safePrev,
          [dayName]: looksLikeBlock ? [prevValue as DayScheduleType] : [defaultBlock]
        } as any
      } else {
        const clone: any = { ...safePrev }
        delete clone[dayName]
        return clone
      }
    })
  }

  
  const isDaySelected = (day: WeekdayOptionFields) => {
    return (selectedDays && selectedDays[day]) ? true : false
  }

  const getDayBlocks = (day: WeekdayOptionFields): DayScheduleType[] => {
    if (!currentResourceOptions) return [];
    const val: any = (currentResourceOptions as any)[day];
    if (!Array.isArray(val)) return [];
    return val as DayScheduleType[];
  }



  const hasOverlap = (blocks: DayScheduleType[]): boolean => {
    for (let i = 0; i < blocks.length; i++) {
      const a = blocks[i];
      // Validate time strings before parsing
      if (!a.open_time || !a.close_time) continue;
      
      const aStart = moment(a.open_time, 'HH:mm A');
      const aEnd = moment(a.close_time, 'HH:mm A');
      
      // Skip invalid moments
      if (!aStart.isValid() || !aEnd.isValid()) continue;
      
      for (let j = i + 1; j < blocks.length; j++) {
        const b = blocks[j];
        
        // Validate time strings before parsing
        if (!b.open_time || !b.close_time) continue;
        
        const bStart = moment(b.open_time, 'HH:mm A');
        const bEnd = moment(b.close_time, 'HH:mm A');
        
        // Skip invalid moments
        if (!bStart.isValid() || !bEnd.isValid()) continue;
        
        if (aStart.isBefore(bEnd) && aEnd.isAfter(bStart)) return true;
      }
    }
    return false;
  }

  const handleBlockTimeChange = (day: WeekdayOptionFields, idx: number, field: 'open_time' | 'close_time', input: string) => {
    // Validate input before processing
    if (!input || input.trim() === '') {
      return; // Don't process empty inputs
    }
    
    let formatted: string;
    try {
      formatted = moment(roundToNearest30Minutes(input), 'HH:mm').format('hh:mmA');
    } catch (error) {
      console.error('Error formatting time:', error);
      return; // Exit early if formatting fails
    }
    
    setCurrentResourceOptions(prev => {
      const prevArr: DayScheduleType[] = getDayBlocks(day);
      const current = prevArr[idx] || { open_time: '08:00 AM', close_time: '08:00 PM', period_type: 'STANDARD' };
      // Enforce close >= open
      let next = { ...current, [field]: formatted } as DayScheduleType;
      
      // Only compare if both values exist and are non-empty strings
      if (field === 'close_time' && current.open_time?.trim() && formatted?.trim() && !isSameOrLess(current.open_time, formatted)) {
        next = { ...next, close_time: current.open_time };
      }
      if (field === 'open_time' && current.close_time?.trim() && formatted?.trim() && !isSameOrLess(formatted, current.close_time)) {
        next = { ...next, close_time: formatted };
      }
      const newArr = prevArr.map((b, i) => (i === idx ? next : b));
      // Prevent overlaps
      if (hasOverlap(newArr)) return prev as any;
      return { ...(prev as any), [day]: newArr } as any;
    })
  }

  const handleBlockPeriodChange = (day: WeekdayOptionFields, idx: number, period: DayScheduleType['period_type']) => {
    setCurrentResourceOptions(prev => {
      const prevArr: DayScheduleType[] = getDayBlocks(day);
      const newArr = prevArr.map((b, i) => (i === idx ? { ...b, period_type: period } : b));
      return { ...(prev as any), [day]: newArr } as any;
    })
  }

  const addBlock = (day: WeekdayOptionFields) => {
    setCurrentResourceOptions(prev => {
      const prevArr: DayScheduleType[] = getDayBlocks(day);
      if (prevArr.length >= 3) return prev as any;
      const last = prevArr[prevArr.length - 1];
      const start = last ? last.close_time : '08:00 AM';
      const end = last ? moment(last.close_time, 'hh:mmA').add(1, 'hour').format('hh:mmA') : '09:00 AM';
      const used = new Set(prevArr.map(b => b.period_type));
      const allPeriods: DayScheduleType['period_type'][] = ['STANDARD','PEAK','OFF_PEAK'];
      const period = allPeriods.find(p => !used.has(p)) || 'STANDARD';
      const newArr = [...prevArr, { open_time: start, close_time: end, period_type: period }];
      if (hasOverlap(newArr)) return prev as any;
      return { ...(prev as any), [day]: newArr } as any;
    })
  }

  const removeBlock = (day: WeekdayOptionFields, idx: number) => {
    setCurrentResourceOptions(prev => {
      const prevArr: DayScheduleType[] = getDayBlocks(day);
      const newArr = prevArr.filter((_, i) => i !== idx);
      return { ...(prev as any), [day]: newArr } as any;
    })
  }


  // Memoized onChange handler function
  // const handleChange = useMemo(() => {
  //   return (event: React.ChangeEvent<HTMLInputElement>) => {
  //       const dayName = event.target.value as WeekdayOptionFields
  //       isDaySelected(dayName);
  //   };
  // }, []); // No dependencies, so the function is memoized only once


  useEffect(()=> {
    // setCurrentResourceOptions(currentResourceOptions as ResourceOptionType)
  }, [currentResourceOptions])

  const { isLoading } = useKitchenHook()
  if (isLoading){
    return (
      <LoadingScreen />
    )
  }


  return (
 
    <div className="flex flex-col">
        <hr className='h-[1px] text-[#E9E9E9] mt-4'/>
        <div className="flex flex-col text-[#555] font-open-sans rounded-md p-2 w-full h-max">
            {
                Array.from(daysOfWeek).map((day: WeekdayOptionFields, i: number) => {
                    return (
                    <OperatingDayHours 
                        key={i} 
                        day={day} 
                        daysOfWeek={daysOfWeek}
                        handleSelectDay={handleSelectDay} 
                        isDaySelected={isDaySelected}
                        getDayBlocks={getDayBlocks}
                        handleBlockTimeChange={handleBlockTimeChange}
                        handleBlockPeriodChange={handleBlockPeriodChange}
                        addBlock={addBlock}
                        removeBlock={removeBlock}
                    />
                    )
                })
            }
        </div>
    </div>
    
  );
};


type OperatingDayHoursProps = {
  day: WeekdayOptionFields
  daysOfWeek: Set<WeekdayOptionFields>
  handleSelectDay: (day: WeekdayOptionFields, value: boolean) => void
  isDaySelected: (day: WeekdayOptionFields) => boolean, 
  getDayBlocks: (day: WeekdayOptionFields) => DayScheduleType[],
  handleBlockTimeChange: (day: WeekdayOptionFields, idx: number, field: 'open_time' | 'close_time', input: string) => void,
  handleBlockPeriodChange: (day: WeekdayOptionFields, idx: number, period: DayScheduleType['period_type']) => void,
  addBlock: (day: WeekdayOptionFields) => void,
  removeBlock: (day: WeekdayOptionFields, idx: number) => void
}



  // Options for the dropdown
  const options: DropdownOption[] = [
    {
      label: "Opened",
      value: true,
      textColor: "text-[#0F973D]",
      bgColor: "bg-[#E7F5EC]",
    },
    {
      label: "Closed",
      value: false,
      textColor: "text-[#D42620]",
      bgColor: "bg-[#FBE9E9]",
    }
  ];


const OperatingDayHours: React.FC<OperatingDayHoursProps> = ({day, handleSelectDay, isDaySelected, getDayBlocks, handleBlockTimeChange, handleBlockPeriodChange, addBlock, removeBlock}) => {
  
 const blocks = getDayBlocks(day)
 const [expanded, setExpanded] = useState<boolean>(false)
 const [currentSelectedOption, setCurrentSelectedOption] = useState<DropdownOption>(options[0]);

 const handleSelectOption = (option: DropdownOption) => {
    const willBeOpen = option.label === 'Opened'
    handleSelectDay(day, willBeOpen)
    setCurrentSelectedOption(option)
    if (willBeOpen) {
      setExpanded(true)
    } else {
      setExpanded(false)
    }
  }

  useEffect(()=> {
    // const getSelectedOption = (d: any) => ((openTime && closeTime) || isDaySelected(d)) ? options[0] :  options[1]
    const selectedOption = (isDaySelected(day) && blocks.length > 0) ? options[0] : options[1]
    setCurrentSelectedOption(selectedOption)
    }, [day, blocks, isDaySelected]
  )
  
  return (
    <>
      <div className="flex flex-row flex-wrap items-start gap-2 text-[14px] font-semibold my-1 p-2 rounded-md border border-[#E9E9E9] bg-white hover:shadow-sm transition w-full">
        <span className="flex flex-row my-auto text-[14px] text-black font-raleway font-medium w-14 min-w-[56px]">
          {getFullDayName(day)}
        </span>
        
        <span className="flex flex-row text-left font-normal ml-auto flex-shrink-0">
          <Dropdown selectedOption={currentSelectedOption} handleSelectOption={handleSelectOption} />
          {/* <div className="flex flex-col gap-1 ml-auto my-auto md:flex-row md:flex-wrap md:items-center rounded-md p-1.5 w-full md:w-auto"> */}
            <span onClick={() => setExpanded(prev => !prev)} className="text-left font-normal cursor-pointer hover:text-primary-400/10">
              {
                (expanded && currentSelectedOption.value ) ? (
                  <ExpandUpIcon className="text-[#F5F5F5] mx-1 cursor-pointer hover:text-primary-400/10" />
                ) : (
                  currentSelectedOption.value ? <ExpandDownIcon className="text-[#F5F5F5] mx-1 cursor-pointer hover:text-primary-400/10" /> : <></>
                )
              }
            </span>
          {/* </div> */}
        </span>
      </div>
      
      {(expanded && currentSelectedOption.value) && (
        <span className="flex flex-col text-left font-normal flex-1 min-w-[220px]">
          {
            (blocks && blocks.length > 0 && isDaySelected(day)) ? (
              blocks.map((b, idx) => {
                // Safely convert time formats with error handling
                let openTime = '';
                let closeTime = '';
                try {
                  if (b.open_time) {
                    const openMoment = moment(b.open_time, 'HH:mm A');
                    openTime = openMoment.isValid() ? openMoment.format('HH:mm') : '';
                  }
                  if (b.close_time) {
                    const closeMoment = moment(b.close_time, 'HH:mm A');
                    closeTime = closeMoment.isValid() ? closeMoment.format('HH:mm') : '';
                  }
                } catch (error) {
                  console.error('Error formatting time for display:', error);
                }
                
                const usedPeriods = new Set(blocks.map((bb, i) => i !== idx ? bb.period_type : undefined));
                const allPeriods: DayScheduleType['period_type'][] = ['STANDARD','PEAK','OFF_PEAK'];
                const availablePeriods = allPeriods.filter(p => !usedPeriods.has(p) || p === b.period_type);
                return (
                  <div key={idx} className="flex flex-col gap-1 my-1 md:flex-row md:flex-wrap md:items-center bg-[#F8F8F8] border-0 rounded-md p-1.5 w-full md:w-auto">
                    {/* period type row */}
                    <div className={`flex items-center gap-1 h-8 md:h-[36px] w-full md:w-40 rounded-md px-2 border-0 ${getPeriodStyles(b.period_type)?.bg} ${getPeriodStyles(b.period_type)?.border} focus-within:outline-none focus-within:ring-0` }>
                      <span className={`h-2 w-2 rounded-full ${getPeriodStyles(b.period_type)?.dot}`} />
                      <select
                        className={`border-none bg-transparent outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 appearance-none w-full text-[11px] font-semibold font-raleway ${getPeriodStyles(b.period_type)?.text}`}
                        value={b.period_type}
                        onChange={e => handleBlockPeriodChange(day, idx, e.target.value as DayScheduleType['period_type'])}
                      >
                        {availablePeriods.map(p => (
                          <option key={p} value={p}>{p.replace('_',' ')}</option>
                        ))}
                      </select>
                    </div>

                    {/* time inputs row */}
                    <div className="flex flex-row items-center gap-1 w-full md:w-auto">
                      <input
                        onChange={e => handleBlockTimeChange(day, idx, 'open_time', e.target.value)}
                        className="h-8 md:h-[32px] flex-1 bg-white border border-[#E9E9E9] rounded-md text-[13px] font-open-sans px-2 min-w-[90px]"
                        type="time"
                        value={openTime}
                        step="1800"
                      />
                      <span className="my-auto text-[#808080]">-</span>
                      <input
                        onChange={e => handleBlockTimeChange(day, idx, 'close_time', e.target.value)}
                        className="h-8 md:h-[32px] flex-1 bg-white border border-[#E9E9E9] rounded-md text-[13px] font-open-sans px-2 min-w-[90px]"
                        type="time"
                        value={closeTime}
                        min={openTime}
                        step="1800"
                      />
                      {blocks.length > 1 && (
                        <button className="ml-1 rounded-full flex items-center justify-center" onClick={() => removeBlock(day, idx)} title="Remove">
                          <DeleteIcon className="text-[#D42620]/50 hover:text-red-600 hover:bg-red-50 rounded-full flex items-center justify-center w-4 h-4"/>
                        </button>
                      )}
                    </div>
                  </div>
                )
              })
            ) : (
              <></>
            )
          }
          {
            (isDaySelected(day) && blocks.length > 0 && blocks.length < 3) && (
              <button className="text-[#013847] font-raleway font-medium text-xs w-max mb-1 flex items-center gap-1 hover:text-primary-400" onClick={() => addBlock(day)}>
                <AddIcon className="my-auto text-[#A1A1A1]" style={{ fontSize: 16 }} /> 
                Add Block
              </button>
            )
          }
        </span>
      )}
    </>
  )
}


interface DropdownOption {
  label: 'Closed' | 'Opened';
  value: boolean;
  textColor: string;
  bgColor: string;
}

type DropdownProps = {
  selectedOption: DropdownOption
  handleSelectOption: (option: DropdownOption) => void
}

const Dropdown: React.FC<DropdownProps> = ({selectedOption, handleSelectOption}) => {
  const [showDropdown, toggleShowDropdown] = useState<boolean>(false)
  
  // Handle selection
  const handleSelectDropdown = (option: DropdownOption) => {
    if (option !== selectedOption){
      handleSelectOption(option);
      toggleShowDropdown(false)
    }
  };

  return (
    <div className="relative">
      {/* Dropdown Button */}
      <button
        onClick={()=>toggleShowDropdown(prev=>!prev)}
        className={`flex items-center justify-between font-open-sans h-8 md:h-[34px] px-3 rounded-full border min-w-[90px] md:min-w-[120px] text-[13px] md:text-[14px] ${
          selectedOption
            ? `${selectedOption.textColor} ${selectedOption.bgColor} border-transparent`
            : "text-gray-700 bg-gray-100 border-[#E9E9E9]"
        }`}
      >
        <span className="text-left my-auto cursor-pointer">{selectedOption ? selectedOption.label : "Closed"} </span>
        <KeyboardArrowDownIcon className="ml-auto my-auto cursor-pointer" style={{ fontSize: 15 }} />
      </button>

      {/* Dropdown Menu */}
      {(showDropdown) && (
      <ul 
        className="absolute min-w-[140px] left-0 right-auto mt-1 border rounded-lg bg-white shadow z-50"
        onMouseLeave={() => toggleShowDropdown(false)}
      >
        {options.map((option, i) => (
          <li
            key={i}
            className={`font-open-sans text-left text-[14px] font-medium cursor-pointer px-4 py-2 hover:bg-gray-50`}
            onClick={() => handleSelectDropdown(option)}
          >
            {option.label}
          </li>
        ))}
      </ul>)}
    </div>
  );
};



export default OperatingHoursList;
